import React, { useState, useEffect, useRef } from 'react';
import { Palette, Type, Upload, Save, X, Loader2, ArrowLeft } from 'lucide-react';
import Button from '../Button';
import Input from '../Input';
import ColorPicker from '../ColorPicker';
import { useAuth } from '../../contexts/AuthContext';
import { Client } from '../../lib/supabase';
import { API_ENDPOINTS } from '../../config/api';

interface ClientBrandSettingsData {
  id?: string;
  client_id: string;
  agency_owner_id: string;
  brand_name: string | null;
  tagline: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ClientBrandSettingsProps {
  client: Client;
  onBack: () => void;
}

const ClientBrandSettings: React.FC<ClientBrandSettingsProps> = ({ client, onBack }) => {
  const { session } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [brandName, setBrandName] = useState('');
  const [tagline, setTagline] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4F46E5');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [accentColor, setAccentColor] = useState('#F59E0B');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch existing client brand settings
  const fetchClientBrandSettings = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.CLIENTS}/${client.id}/brand-settings`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.brandSettings) {
          const settings = data.brandSettings;
          setBrandName(settings.brand_name || client.company_name || '');
          setTagline(settings.tagline || '');
          setPrimaryColor(settings.primary_color);
          setSecondaryColor(settings.secondary_color);
          setAccentColor(settings.accent_color);
          setLogoUrl(settings.logo_url);
          setPreviewUrl(settings.logo_url);
        } else {
          // Set defaults based on client info
          setBrandName(client.company_name || client.name || '');
        }
      }
    } catch (error) {
      console.error('Error fetching client brand settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientBrandSettings();
  }, [client.id]);

  const processLogoFile = (file?: File) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload PNG, JPG, or SVG files only.' });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 10MB.' });
      return;
    }

    setMessage(null);
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    processLogoFile(e.target.files?.[0]);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    }

    if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    processLogoFile(e.dataTransfer.files?.[0]);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setPreviewUrl(null);
    setLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const brandSettings = {
        brand_name: brandName || null,
        tagline: tagline || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        logo_url: logoUrl,
        logo_file: logoFile
      };

      const formData = new FormData();
      formData.append('brandSettings', JSON.stringify(brandSettings));
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await fetch(`${API_ENDPOINTS.AGENCY.CLIENTS}/${client.id}/brand-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.brandSettings) {
          setLogoUrl(data.brandSettings.logo_url);
          setPreviewUrl(data.brandSettings.logo_url);
        }
        setLogoFile(null);
        setMessage({ type: 'success', text: 'Client brand settings saved successfully!' });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save brand settings');
      }
    } catch (error: any) {
      console.error('Error saving client brand settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save brand settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="tertiary"
          size="small"
          onClick={onBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Clients
        </Button>
        
        <h1 className="text-h2 font-heading text-text-dark mb-2">
          Brand Settings - {client.company_name || client.name}
        </h1>
        <p className="text-body text-body-grey">
          Customize brand identity for {client.name}'s mockups
        </p>
      </div>

      <div className="space-y-8">
        {/* Brand Information */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Type className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Brand Name
              </label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter client's brand name"
              />
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Tagline
              </label>
              <Input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Enter client's brand tagline"
              />
            </div>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Palette className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Primary Color
              </label>
              <ColorPicker
                color={primaryColor}
                onChange={setPrimaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Secondary Color
              </label>
              <ColorPicker
                color={secondaryColor}
                onChange={setSecondaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: secondaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Accent Color
              </label>
              <ColorPicker
                color={accentColor}
                onChange={setAccentColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: accentColor }}></div>
            </div>
          </div>
        </div>

        {/* Brand Logo */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Logo
          </h2>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {previewUrl ? (
            <div className="relative">
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                <img 
                  src={previewUrl} 
                  alt="Client brand logo preview" 
                  className="max-h-32 mx-auto object-contain"
                />
              </div>
              <button
                onClick={handleRemoveLogo}
                className="absolute top-2 right-2 p-1 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
              >
                <X className="w-5 h-5 text-red-600" />
              </button>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {logoFile ? 'New logo selected' : 'Current client logo'}
              </p>
            </div>
          ) : (
            <div 
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-brand-purple bg-brand-purple/5'
                  : 'border-gray-300 hover:border-brand-purple'
              }`}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Upload client's brand logo</p>
              <p className="text-sm text-gray-500 mb-2">PNG, JPG or SVG. Max 10MB</p>
              <p className="text-xs text-gray-400 mb-4">Drag and drop a logo here, or choose a file</p>
              <Button variant="secondary" size="small" onClick={() => {
                fileInputRef.current?.click();
              }}>
                Choose File
              </Button>
            </div>
          )}
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            variant="primary" 
            size="large"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Brand Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientBrandSettings;
